import { hashKey } from '@tanstack/react-query';

const CLIENT_KEY = 'servwell-client-id';

/**
 * Foundation Rule 3: every mutation carries a client-minted idempotency key, so a
 * double-tapped "Move visit" on flaky LTE — or a mutation replayed when the PWA comes
 * back online — applies exactly once (the server dedupes on (clientId, mutationId)).
 *
 * clientId identifies this browser and is stable across reloads; mutationId is minted
 * per user intent.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function clientId(): string {
  const stored = localStorage.getItem(CLIENT_KEY);
  // Checked, not merely present. The server's `client_id` is a `uuid` column, so a value
  // that is not one is dropped on arrival — and a write with no key is a write that dedupes
  // nothing, silently, which is the exact failure this whole rule exists to prevent.
  if (stored && UUID_RE.test(stored)) return stored;
  const id = crypto.randomUUID();
  localStorage.setItem(CLIENT_KEY, id);
  return id;
}

/** Spread into any mutation input: `{ ...idem(), visitId }`. */
export function idem(): { clientId: string; mutationId: string } {
  return { clientId: clientId(), mutationId: crypto.randomUUID() };
}

/** A form owns its last submitted intent until accepted or explicitly abandoned.
 * Include account, action and the complete payload. Editing after submission creates
 * a new intent, even if the person later types the earlier values again. */
export function createIntentKey(generate = idem) {
  let last: { hash: string; key: ReturnType<typeof idem> } | undefined;
  return {
    forIntent(input: readonly unknown[]) {
      const hash = hashKey(input);
      if (!last || last.hash !== hash) last = { hash, key: generate() };
      return last.key;
    },
    clear() {
      last = undefined;
    },
  };
}

/**
 * A timer tap's key and its moment (build-map 062): `{ ...tap(), timeEntryId }`. The moment is kept
 * with the tap in the offline queue; `tapAgeLink` sends it as how long ago it was, so a Start or
 * Stop sent later is recorded when it happened, whatever the phone's clock says.
 */
export function tap(): {
  clientId: string;
  mutationId: string;
  at: Date;
  mono: number;
  session: string;
} {
  // `mono` is this page's monotonic clock, which a change to the phone's clock never moves: the
  // age is read from it while the page lives (`tapAgeLink`), and from `at` only after a reload.
  return { ...idem(), at: new Date(), mono: performance.now(), session: PAGE_SESSION };
}

/** This page load, so a tap's monotonic stamp is read only by the page that made it (062). */
export const PAGE_SESSION: string = crypto.randomUUID();

/** A write's own idempotency key, if it carries one: both halves, non-empty strings. */
export function keyOf(
  variables: unknown,
): { clientId: string; mutationId: string } | null {
  const v = variables as { clientId?: unknown; mutationId?: unknown } | null | undefined;
  if (typeof v?.clientId !== 'string' || typeof v.mutationId !== 'string') return null;
  if (!v.clientId || !v.mutationId) return null;
  return { clientId: v.clientId, mutationId: v.mutationId };
}

/** One string for a key: `clientId:mutationId`, the pair the server dedupes on. */
export function idOf(key: { clientId: string; mutationId: string }): string {
  return `${key.clientId}:${key.mutationId}`;
}
